import { itx } from "../../../src/translationItx/itx";
import { loadPdfDocumentHeader, type PdfDocumentHeader } from "../../../src/pdf/PdfDocumentHeader";
import { protectPdfOutput } from "../../../src/pdf/protectPdfOutput";
import { keepPdfBlockTogether } from "../../../src/pdf/pdfPagination";
import { renderToStaticMarkup } from "react-dom/server";
import { jsPDF } from "jspdf";
import "svg2pdf.js";
import { PDFDocument } from "pdf-lib";
import { deduplicateC1PdfImages } from "../lib/C1_PdfImageDeduplication";
import { C1_DOCUMENT_LOGO, C1_PRODUCT_NAME, ESTADA_PDF_COVER_LOGO } from "../C1_Const";
import type { C1_ButtonProperties } from "../types/C1_EloTypes";
import { getButtonProductDefinition } from "../data/C1_ProductDefinitions";
import { ButtonFrontView } from "../frontViews/ButtonFrontview";
import type { RockerRowConfig } from "../frontViews/GlasButtonFrontView";
import { WallOutletSvg, type WallOutletSlotType } from "../frontViews/WallOutletFrontview";
import { getC1DocumentationMarkdown, createC1MarkdownCoverPdfBlob } from "./C1_RaumbuchCoverMarkdown";
import type { C1_RoombookProjectSummary } from "./C1_RaumbuchSummary";
import {
  getC1RoombookWiringCountExplanation,
  formatC1RoombookSummaryValue,
  hasC1RoombookSummaryWiringCounts,
  visibleC1RoombookSummaryColumns,
} from "./C1_RaumbuchSummary";
import { addC1PdfOutlines, addC1PdfPageLinks, type C1_PdfOutlineEntry } from "../lib/C1_PdfOutlines";
import { createC1PdfTableOfContentsBlob } from "../lib/C1_PdfTableOfContents";
import type { C1_PdfChapterArtifact } from "../lib/C1_PdfChapter";
import { c1PdfFileName } from "../lib/C1_PdfFileName";

export type C1_RaumbuchPdfButton = {
  productId?: string;
  properties: C1_ButtonProperties;
  rows: RockerRowConfig[];
};

export type C1_RaumbuchPdfCategory = {
  title: string;
  details?: string;
  level?: number;
  outletSlots?: WallOutletSlotType[];
  items: Array<{
    tag: string;
    type?: string;
    name: string;
    details: string;
    button?: C1_RaumbuchPdfButton;
    consumerImage?: string;
  }>;
};

export type C1_RaumbuchPdfRoom = {
  id: string;
  floorLabel: string;
  roomLabel: string;
  fullTag?: string;
  floorTag?: string;
  roomTag?: string;
  floorName?: string;
  roomName?: string;
  categories: C1_RaumbuchPdfCategory[];
  notes?: string[];
};

type C1_RaumbuchPdfOptions = {
  projectName: string;
  dateText: string;
  mode: "functions" | "groups";
  rooms: C1_RaumbuchPdfRoom[];
  summary?: C1_RoombookProjectSummary;
};

const PDF_PRODUCT_NAME = `ESTADA ${C1_PRODUCT_NAME}`;
const colors = {
  ink: "#172b3a",
  muted: "#586a75",
  line: "#aeb9bf",
  softLine: "#d9e0e4",
  fill: "#eef2f4",
};
const margin = 45;
const contentTop = 82;
const footerTopOffset = 48;

/** Loads the original consumer symbol without making a missing optional image abort the report. */
function loadRoombookConsumerImage(source: string): Promise<HTMLImageElement | undefined> {
  return new Promise(resolve => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => resolve(undefined);
    image.src = source;
  });
}



function buttonSvg(button: C1_RaumbuchPdfButton): SVGSVGElement {
  const product = getButtonProductDefinition(button.productId);
  const markup = renderToStaticMarkup(
    <ButtonFrontView
      uiTemplate={product?.uiTemplate ?? "simple"}
      rows={button.rows}
      width={button.properties.widthMm}
      height={button.properties.heightMm}
      color={button.properties.color ?? "white"}
      orientation={button.properties.orientation ?? "vertical"}
      hasDisplay={button.properties.hasDisplay ?? false}
      usePhysicalSize={false}
    />,
  );
  return new DOMParser().parseFromString(markup, "image/svg+xml").documentElement as unknown as SVGSVGElement;
}

function wallOutletSvg(slots: WallOutletSlotType[]): SVGSVGElement {
  const markup = renderToStaticMarkup(<WallOutletSvg slots={slots} />);
  return new DOMParser().parseFromString(markup, "image/svg+xml").documentElement as unknown as SVGSVGElement;
}

/** Supplies room identity and report mode to the shared document header. */
function drawHeader(document: jsPDF, title: { floorLabel: string; roomLabel: string },
  mode: C1_RaumbuchPdfOptions["mode"], header: PdfDocumentHeader) {
  header.draw(document, { planType: [itx("pageDocumentType.roombook", "Raumbuch"), mode === "groups" ? itx("components.plural.group", "Gruppen") : itx("c1.ui.Funktionen", "Funktionen")],
    hierarchy: [title.floorLabel, title.roomLabel] });
}

function drawFooter(document: jsPDF, options: C1_RaumbuchPdfOptions, pageNumber: number, totalPages: number) {
  const pageWidth = document.internal.pageSize.getWidth();
  const pageHeight = document.internal.pageSize.getHeight();
  const y = pageHeight - footerTopOffset;
  document.setDrawColor(colors.line);
  document.setLineWidth(1);
  document.line(margin, y, pageWidth - margin, y);
  document.setTextColor(colors.muted);
  document.setFont("helvetica", "normal");
  document.setFontSize(6.5);
  document.text(itx("c1.ui.Projekt_value1", "Projekt: {{value1}}", { "{{value1}}": String(options.projectName) }), margin, y + 14);
  document.text(itx("c1.ui.Stand_value1", "Stand: {{value1}}", { "{{value1}}": String(options.dateText) }), pageWidth / 2, y + 14, { align: "center" });
  document.text(`${pageNumber + 2} / ${totalPages + 2}`, pageWidth - margin, y + 14, { align: "right" });
}

function fillRect(document: jsPDF, x: number, y: number, width: number, height: number, color: string) {
  document.setFillColor(color);
  document.setDrawColor(colors.line);
  document.rect(x, y, width, height, "FD");
}

async function drawRoomPages(
  document: jsPDF,
  room: C1_RaumbuchPdfRoom,
  options: C1_RaumbuchPdfOptions,
  header: PdfDocumentHeader,
  firstPage: boolean,
) {
  if (!firstPage) document.addPage("a4", "portrait");
  drawHeader(document, room, options.mode, header);
  const pageWidth = document.internal.pageSize.getWidth();
  const pageHeight = document.internal.pageSize.getHeight();
  const width = pageWidth - margin * 2;
  const bottom = pageHeight - 60;
  let y = contentTop;
  const drawColumnHeader = () => {
    if (options.mode !== "functions" || !room.categories.length) return;
    document.setDrawColor(colors.softLine);
    document.rect(margin, y, width, 19);
    document.setFont("helvetica", "bold");
    document.setFontSize(6);
    document.setTextColor(colors.muted);
    document.text(itx("ColumnFilterUI.tag", "Tag").toUpperCase(), margin + 9, y + 12);
    document.text(itx("name", "Name").toUpperCase(), margin + width * 0.26 + 9, y + 12);
    document.text(itx("c1.ui.AUSFUHRUNG", "AUSFÜHRUNG"), margin + width * 0.55 + 9, y + 12);
    y += 19;
  };
  const nextPage = () => {
    document.addPage("a4", "portrait");
    drawHeader(document, room, options.mode, header);
    y = contentTop;
    drawColumnHeader();
  };
  drawColumnHeader();

  for (const category of room.categories) {
    const indent = options.mode === "groups" ? (category.level ?? 0) * 18 : 0;
    const x = margin + indent;
    const categoryWidth = width - indent;
    const categoryHeaderHeight = category.outletSlots?.length ? 52 : 27;
    const tagWidth = options.mode === "groups" ? 0.18 : 0.26;
    const typeWidth = options.mode === "groups" ? 0.22 : 0;
    const nameWidth = options.mode === "groups" ? 0.26 : 0.29;
    const detailX = x + categoryWidth * (tagWidth + typeWidth + nameWidth);
    document.setFont("helvetica", "normal");
    document.setFontSize(8);
    const measuredItems = category.items.map(item => {
      const textX = detailX + 9 + (item.button ? 48 : item.consumerImage ? 30 : 0);
      const detailLines: string[] = document.splitTextToSize(item.details, x + categoryWidth - textX - 9);
      const rowHeight = Math.max(item.button ? 46 : item.consumerImage ? 32 : 25, detailLines.length * 10 + 12);
      return { item, textX, detailLines, rowHeight };
    });
    keepPdfBlockTogether(y, bottom, [categoryHeaderHeight, measuredItems[0]?.rowHeight ?? 0], nextPage);
    fillRect(document, x, y, categoryWidth, categoryHeaderHeight, colors.fill);
    document.setTextColor(colors.ink);
    document.setFont("helvetica", "bold");
    document.setFontSize(9);
    document.text(category.title, x + 9, y + 17);
    const titleEndX = x + 9 + document.getTextWidth(category.title);
    if (category.details) {
      document.setFont("helvetica", "normal");
      document.setFontSize(7);
      document.setTextColor(colors.muted);
      const detailsX = titleEndX + 5;
      const detailsRight = category.outletSlots?.length ? x + categoryWidth * 0.66 : x + categoryWidth;
      document.text(`· ${category.details}`, detailsX, y + 17, {
        maxWidth: Math.max(0, detailsRight - detailsX - 9),
      });
    }
    if (category.outletSlots?.length) {
      const executionX = x + categoryWidth * 0.66;
      const availableWidth = x + categoryWidth - executionX - 18;
      const aspectRatio =
        (20 + category.outletSlots.length * 100 + Math.max(0, category.outletSlots.length - 1) * 6) / 120;
      const previewWidth = Math.min(availableWidth, 40 * aspectRatio);
      const previewHeight = previewWidth / aspectRatio;
      await document.svg(wallOutletSvg(category.outletSlots), {
        x: executionX + 9,
        y: y + (categoryHeaderHeight - previewHeight) / 2,
        width: previewWidth,
        height: previewHeight,
      });
    }
    y += categoryHeaderHeight;

    for (const { item, textX, detailLines, rowHeight } of measuredItems) {
      document.setFont("helvetica", "normal");
      document.setFontSize(8);
      if (y + rowHeight > bottom) {
        nextPage();
        fillRect(document, x, y, categoryWidth, 22, colors.fill);
        document.setFont("helvetica", "bold");
        document.setFontSize(8);
        document.setTextColor(colors.ink);
        document.text(itx("c1.ui.value1_Fortsetzung", "{{value1}} (Fortsetzung)", { "{{value1}}": String(category.title) }), x + 9, y + 14);
        y += 22;
      }
      document.setDrawColor(colors.softLine);
      document.rect(x, y, categoryWidth, rowHeight);
      const baseline = y + rowHeight / 2 + 3;
      document.setTextColor(colors.ink);
      document.setFont("helvetica", "bold");
      document.setFontSize(8);
      document.text(item.tag, x + 9, baseline);
      let cursor = x + categoryWidth * tagWidth;
      if (options.mode === "groups") {
        document.setFont("helvetica", "normal");
        document.text(item.type ?? "", cursor + 9, baseline);
        cursor += categoryWidth * typeWidth;
      }
      document.setFont("helvetica", "normal");
      document.setFontSize(8);
      document.setTextColor(colors.ink);
      document.text(item.name, cursor + 5, baseline, {
        maxWidth: categoryWidth * nameWidth - 10,
      });
      if (item.button) {
        const iconSize = 36;
        await document.svg(buttonSvg(item.button), {
          x: detailX + 9,
          y: y + (rowHeight - iconSize) / 2,
          width: iconSize,
          height: iconSize,
        });
      }
      if (item.consumerImage) {
        const image = await loadRoombookConsumerImage(item.consumerImage);
        if (image) {
          const scale = (24 * 0.7) / Math.max(image.naturalWidth, image.naturalHeight);
          const width = image.naturalWidth * scale;
          const height = image.naturalHeight * scale;
          document.addImage(image, detailX + 9 + (24 - width) / 2, y + (rowHeight - height) / 2, width, height);
        }
      }
      document.setTextColor(colors.muted);
      document.text(detailLines, textX, baseline - (detailLines.length - 1) * 5, { lineHeightFactor: 1.25 });
      y += rowHeight;
    }
    y += 12;
  }
  if (room.notes?.length) {
    const notesHeaderHeight = 27;
    keepPdfBlockTogether(y, bottom, [notesHeaderHeight, 12, 12], nextPage);
    fillRect(document, margin, y, width, notesHeaderHeight, colors.fill);
    document.setFont("helvetica", "bold");
    document.setFontSize(9);
    document.setTextColor(colors.ink);
    document.text(itx("c1.ui.Notizen", "Notizen"), margin + 9, y + 17);
    y += notesHeaderHeight;
    for (const [noteIndex, note] of room.notes.entries()) {
      document.setFont("helvetica", "normal");
      document.setFontSize(8);
      const lines = document.splitTextToSize(note, width - 18) as string[];
      const topMargin = noteIndex === 0 ? 12 : 20;
      keepPdfBlockTogether(y, bottom, [topMargin, Math.min(lines.length, 2) * 12], nextPage);
      if (noteIndex > 0) {
        document.setDrawColor(colors.softLine);
        document.setLineWidth(0.5);
        document.line(margin + 9, y + 6, margin + width - 9, y + 6);
      }
      y += topMargin;
      for (const line of lines) {
        if (y + 12 > bottom) nextPage();
        document.setFont("helvetica", "normal");
        document.setFontSize(8);
        document.setTextColor(colors.ink);
        document.text(line, margin + 9, y + 9);
        y += 12;
      }
    }
  }
}

function drawSummaryPages(document: jsPDF, options: C1_RaumbuchPdfOptions, header: PdfDocumentHeader) {
  if (!options.summary) return;
  const columns = visibleC1RoombookSummaryColumns(options.summary);
  const rows = options.summary.floors.flatMap((floor) => [
    { label: floor.label, values: undefined, total: undefined, heading: true },
    ...floor.rows,
    floor.total,
  ]);
  document.addPage("a4", "landscape");
  const pageWidth = document.internal.pageSize.getWidth();
  const pageHeight = document.internal.pageSize.getHeight();
  const title = { floorLabel: options.projectName, roomLabel: "Zusammenfassung" };
  drawHeader(document, title, options.mode, header);
  let y = contentTop;
  const tableWidth = pageWidth - margin * 2;
  const labelWidth = 115;
  const totalWidth = 34;
  const valueWidth = (tableWidth - labelWidth - totalWidth) / Math.max(1, columns.length);
  const columnGroups = columns.reduce<Array<{ label: string; start: number; count: number }>>(
    (groups, column, index) => {
      const last = groups.at(-1);
      if (last?.label === column.group) last.count += 1;
      else groups.push({ label: column.group, start: index, count: 1 });
      return groups;
    },
    [],
  );
  const drawColumnHeader = () => {
    const headerHeight = 40;
    fillRect(document, margin, y, tableWidth, headerHeight, colors.fill);
    document.setFont("helvetica", "bold");
    document.setFontSize(5.2);
    document.setTextColor(colors.muted);
    document.text("RAUM", margin + 5, y + 24);
    columnGroups.forEach((group) => {
      const center = margin + labelWidth + valueWidth * (group.start + group.count / 2);
      document.text(group.label.toUpperCase(), center, y + 12, { align: "center" });
    });
    columns.forEach((column, index) =>
      document.text(column.label || itx("count", "Anzahl"), margin + labelWidth + valueWidth * (index + 0.5), y + 28, {
        align: "center",
      }),
    );
    document.text("SUMME", margin + tableWidth - totalWidth / 2, y + 24, { align: "center" });
    y += headerHeight;
  };
  drawColumnHeader();
  for (const row of rows) {
    keepPdfBlockTogether(y, pageHeight - 60, [22, "heading" in row && row.heading ? 22 : 0], () => {
      document.addPage("a4", "landscape");
      drawHeader(document, title, options.mode, header);
      y = contentTop;
      drawColumnHeader();
    });
    if ("heading" in row && row.heading) {
      document.setFont("helvetica", "bold");
      document.setFontSize(9);
      document.setTextColor(colors.ink);
      document.text(row.label, margin, y + 15);
      y += 22;
      continue;
    }
    document.setDrawColor(colors.softLine);
    document.rect(margin, y, tableWidth, 22);
    document.setFont("helvetica", "normal");
    document.setFontSize(6);
    document.setTextColor(colors.ink);
    document.text(row.label, margin + 5, y + 14);
    columns.forEach((column, index) =>
      document.text(
        row.values ? formatC1RoombookSummaryValue(row, column) : "–",
        margin + labelWidth + valueWidth * (index + 0.5),
        y + 14,
        { align: "center" },
      ),
    );
    document.setFont("helvetica", "bold");
    document.text(String(row.total || "–"), margin + tableWidth - totalWidth / 2, y + 14, { align: "center" });
    y += 22;
  }
  if (hasC1RoombookSummaryWiringCounts(options.summary)) {
    if (y + 34 > pageHeight - 60) {
      document.addPage("a4", "landscape");
      drawHeader(document, title, options.mode, header);
      y = contentTop;
    }
    document.setFont("helvetica", "normal");
    document.setFontSize(6.5);
    document.setTextColor(colors.muted);
    document.text(getC1RoombookWiringCountExplanation(), margin, y + 16, {
      maxWidth: tableWidth,
    });
  }
}

export async function downloadC1RaumbuchPdf(
  options: C1_RaumbuchPdfOptions,
  download = true,
  chapterOnly = false,
): Promise<C1_PdfChapterArtifact> {
  const document = new jsPDF({ orientation: "portrait", unit: "pt", format: "a4", compress: true });
  document.setProperties({ title: `${PDF_PRODUCT_NAME} ${itx("pageDocumentType.roombook", "Raumbuch")}`, author: "ESTADA" });
  const header = await loadPdfDocumentHeader();
  const roomSections: Array<{ id: string; floorLabel: string; roomLabel: string; pageIndex: number }> = [];
  for (let index = 0; index < options.rooms.length; index += 1) {
    roomSections.push({
      id: options.rooms[index].id,
      floorLabel: options.rooms[index].floorLabel,
      roomLabel: options.rooms[index].roomLabel,
      pageIndex: index === 0 ? 0 : document.getNumberOfPages(),
    });
    await drawRoomPages(document, options.rooms[index], options, header, index === 0);
  }
  const summaryPageIndex = document.getNumberOfPages();
  drawSummaryPages(document, options, header);
  const tableOfContentsEntries = [
    ...Array.from(new Set(roomSections.map((section) => section.floorLabel))).flatMap((floorLabel) => {
      const floorRooms = roomSections.filter((section) => section.floorLabel === floorLabel);
      return [
        { title: floorLabel, contentPageIndex: floorRooms[0].pageIndex, level: 1 as const },
        ...floorRooms.map((section) => ({
          title: section.roomLabel,
          contentPageIndex: section.pageIndex,
          level: 2 as const,
        })),
      ];
    }),
    ...(options.summary ? [{ title: itx("TopologyLineView.summary", "Zusammenfassung"), contentPageIndex: summaryPageIndex, level: 1 as const }] : []),
  ];
  const tableOfContents = await createC1PdfTableOfContentsBlob({
    projectName: options.projectName,
    entries: tableOfContentsEntries,
    pageSize: "A4",
    orientation: "portrait",
  });
  const contentPages = document.getNumberOfPages();
  for (let page = 1; page <= contentPages; page += 1) {
    document.setPage(page);
    drawFooter(document, options, page + tableOfContents.pageCount, contentPages + tableOfContents.pageCount);
  }

  const chapterPages = Array.from({ length: contentPages }, (_, pageIndex) => {
    const section = [...roomSections].reverse().find((entry) => entry.pageIndex <= pageIndex);
    if (!section || pageIndex >= summaryPageIndex) {
      return { title: itx("TopologyLineView.summary", "Zusammenfassung"), pageIndex };
    }
    return {
      title: `${section.roomLabel}${pageIndex > section.pageIndex ? itx("c1.ui.Fortsetzung", " · Fortsetzung") : ""}`,
      sectionTitle: section.floorLabel,
      sourceIds: [section.id],
      pageIndex,
    };
  });
  const contentBlob = await protectPdfOutput(document.output("blob"), "list");
  if (chapterOnly) return { blob: contentBlob, pages: chapterPages };

  const coverBlob = await createC1MarkdownCoverPdfBlob({
    documentName: itx("pageDocumentType.roombook", "Raumbuch"),
    projectName: options.projectName,
    dateText: options.dateText,
    logo: ESTADA_PDF_COVER_LOGO,
    pageSize: "A4",
    orientation: "portrait",
  });
  const documentationBlob = await createC1MarkdownCoverPdfBlob({
    documentName: itx("pageDocumentType.roombook", "Raumbuch"),
    markdownTemplate: getC1DocumentationMarkdown(),
    textAlign: "left",
    projectName: options.projectName,
    dateText: options.dateText,
    logo: C1_DOCUMENT_LOGO,
    pageSize: "A4",
    orientation: "portrait",
  });
  const merged = await PDFDocument.create();
  for (const source of [coverBlob, documentationBlob, tableOfContents.blob]) {
    const sourceDocument = await PDFDocument.load(await source.arrayBuffer());
    const sourcePages = await merged.copyPages(sourceDocument, sourceDocument.getPageIndices());
    sourcePages.forEach((page) => merged.addPage(page));
  }
  const contentDocument = await PDFDocument.load(await contentBlob.arrayBuffer());
  const content = await merged.copyPages(contentDocument, contentDocument.getPageIndices());
  content.forEach((page) => merged.addPage(page));
  addC1PdfOutlines(merged, [
    { title: itx("c1.ui.Deckblatt", "Deckblatt"), pageIndex: 0 },
    { title: itx("c1.ui.Dokumentation", "Dokumentation"), pageIndex: 1 },
    { title: itx("pageDocumentType.coverPage", "Inhaltsverzeichnis"), pageIndex: 2 },
    ...Array.from(new Set(roomSections.map((section) => section.floorLabel))).map((floorLabel) => {
      const floorRooms = roomSections.filter((section) => section.floorLabel === floorLabel);
      return {
        title: floorLabel,
        pageIndex: floorRooms[0].pageIndex + 2 + tableOfContents.pageCount,
        children: floorRooms.map((section) => ({
          title: section.roomLabel,
          pageIndex: section.pageIndex + 2 + tableOfContents.pageCount,
        })),
      } satisfies C1_PdfOutlineEntry;
    }),
    ...(options.summary ? [{
      title: itx("TopologyLineView.summary", "Zusammenfassung"),
      pageIndex: summaryPageIndex + 2 + tableOfContents.pageCount,
    }] : []),
  ]);
  addC1PdfPageLinks(
    merged,
    tableOfContents.links.map((link) => ({
      ...link,
      sourcePageIndex: 2 + link.tableOfContentsPageIndex,
      targetPageIndex: 2 + tableOfContents.pageCount + link.contentPageIndex,
    })),
  );
  await deduplicateC1PdfImages(merged);
  const bytes = await merged.save();
  const blob = new Blob([Uint8Array.from(bytes)], { type: "application/pdf" });
  if (download) {
    const url = URL.createObjectURL(blob);
    const link = window.document.createElement("a");
    link.href = url;
    link.download = c1PdfFileName(options.projectName);
    link.click();
    window.setTimeout(() => URL.revokeObjectURL(url), 1_000);
  }
  return { blob, pages: chapterPages };
}
